/**
 * The browser half of the session timeline: route changes and clicks, labelled by accessible name,
 * batched into `client_action` events and kept as a crash report's breadcrumbs. Never keystrokes,
 * input values or DOM state: this is not session replay.
 */
import { recordClientActionsFn } from './data';
import { telemetryHeaders } from './telemetry-headers';
// Type-only, so no monolith code reaches the client bundle; shared so a field cannot exist on one side only.
import type { ClientAction, ClientActionType, ClientErrorBreadcrumb } from '@worldcoo/monolith/wide-events';

/**
 * `sanitizeLabel` cannot recognise a person's NAME, so a subtree rendering donor data carries this and
 * its clicks are labelled structurally. Put it on the container, not on every control.
 */
export const TELEMETRY_OPT_OUT_ATTRIBUTE = 'data-no-telemetry';
const TELEMETRY_OPT_OUT_SELECTOR = `[${TELEMETRY_OPT_OUT_ATTRIBUTE}]`;

const MAX_LABEL_LEN = 120;
const BATCH_AT_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
/** Enough for context, small enough to stay a side note on the crash report. */
const BREADCRUMB_LIMIT = 20;

let pending: ClientAction[] = [];
let breadcrumbs: ClientAction[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;

/** Oldest first. */
export function recentBreadcrumbs(): ClientErrorBreadcrumb[] {
	return breadcrumbs.map((action) => ({ t: action.t, type: action.type, message: `${action.label} (${action.path})` }));
}

/**
 * The browser cancels in-flight requests on unload, and that batch is the last thing the user did.
 * `sendBeacon` cannot carry the server function's serialized payload and headers.
 */
const keepaliveFetch = (...args: Parameters<typeof fetch>): Promise<Response> =>
	fetch(args[0], { ...args[1], keepalive: true });

function flush(options?: { unloading?: boolean }): void {
	if (flushTimer) {
		clearTimeout(flushTimer);
		flushTimer = undefined;
	}
	if (pending.length === 0) return;
	const batch = pending;
	pending = [];
	recordClientActionsFn({
		data: { actions: batch },
		// Unload only: keepalive bodies share a 64KB per-page budget. A per-call `fetch` REPLACES the app-wide
		// one that attaches the telemetry headers (`opts.fetch ?? startOptions.serverFns.fetch`), so pass them.
		...(options?.unloading ? { fetch: keepaliveFetch, headers: telemetryHeaders() } : {}),
	}).catch(() => {
		// Dropped, not retried: a retry queue costs more than best-effort telemetry is worth.
	});
}

function scheduleFlush(): void {
	if (flushTimer) return;
	flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

const SIX_PLUS_DIGITS_WITH_SPACES_OR_DASHES = /\b[\d][\d\s-]{4,}\d\b/g;

/**
 * Emails, long digit runs (card, phone, document, account numbers) and IBANs become placeholders.
 * Deliberately coarse: under-redacting writes a donor's email into durable analytics storage.
 */
export function sanitizeLabel(label: string): string {
	return label
		.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
		.replace(/\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/g, '[iban]')
		.replace(SIX_PLUS_DIGITS_WITH_SPACES_OR_DASHES, '[number]')
		.replace(/\s+/g, ' ')
		.trim();
}

function record(type: ClientActionType, label: string, path: string): void {
	if (typeof window === 'undefined') return;
	const action: ClientAction = { t: Date.now(), type, label: sanitizeLabel(label).slice(0, MAX_LABEL_LEN), path };
	breadcrumbs.push(action);
	if (breadcrumbs.length > BREADCRUMB_LIMIT) breadcrumbs = breadcrumbs.slice(-BREADCRUMB_LIMIT);
	pending.push(action);
	if (pending.length >= BATCH_AT_SIZE) flush();
	else scheduleFlush();
}

/** Null when the click hit no interactive element. */
function interactiveLabel(target: EventTarget | null): string | null {
	if (!(target instanceof Element)) return null;
	// A click usually lands on the text or icon INSIDE a button.
	const interactive = target.closest('a, button, [role="button"], [role="link"], summary');
	if (!interactive) return null;
	if (interactive.closest(TELEMETRY_OPT_OUT_SELECTOR)) return `<${interactive.tagName.toLowerCase()}>`;
	const label =
		interactive.getAttribute('aria-label') ??
		interactive.getAttribute('title') ??
		// Never `value`: the visible label of what the user pressed.
		interactive.textContent?.trim();
	// An icon-only control with no accessible name still records that something was pressed.
	return label || `<${interactive.tagName.toLowerCase()}>`;
}

let installed = false;

/** `subscribeToNavigations` is passed in so this module need not know the router. */
export function installSessionActionTracking(
	subscribeToNavigations: (onNavigate: (path: string) => void) => void,
): void {
	if (installed || typeof window === 'undefined') return;
	installed = true;

	record('nav', 'initial-load', window.location.pathname);
	subscribeToNavigations((path) => {
		record('nav', path, path);
	});

	// Capture phase: the clicks worth seeing before a crash are often ones whose handler stops propagation.
	window.addEventListener(
		'click',
		(event) => {
			try {
				const label = interactiveLabel(event.target);
				if (label) record('click', label, window.location.pathname);
			} catch {
				// Never let telemetry interfere with a click.
			}
		},
		{ capture: true, passive: true },
	);

	// `pagehide` covers the bfcache path that never fires `visibilitychange`.
	window.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') flush({ unloading: true });
	});
	window.addEventListener('pagehide', () => {
		flush({ unloading: true });
	});
}
